import React, { type ReactNode, useMemo } from 'react'
import { type ControllerFieldState, type FieldValues, useController, type UseControllerProps } from 'react-hook-form'
import { type ControllerRenderProps } from 'react-hook-form/dist/types/controller'
import clsx from 'clsx'
import { Box, FormControlLabel, FormGroup, Switch, TextField, Tooltip, Typography, useTheme } from '@mui/material'
import { type IStrategyComponent } from '../../../models/strategy/strategy-component-types'
import { type StrategyComponentFormInputs } from './strategy-component-card'

interface StrategyComponentParameterCardProps extends UseControllerProps<StrategyComponentFormInputs> {
  strategy: IStrategyComponent | undefined
  parameter: string
  value: string | number | boolean
}

function createStringTextField (fieldId: string, label: string, field: ControllerRenderProps<FieldValues, string>, fieldState: ControllerFieldState): ReactNode {
  return <TextField
    {...field}
    id={`${fieldId}-text-field`}
    key={`${fieldId}-text-field`}
    label={label}
    type="string"
    size="small"
    sx={{ width: '150px' }}
    value={field.value ?? ''}
    InputLabelProps={{
      shrink: true
    }}
    className={clsx('fieldState', {
      dirty: fieldState?.isDirty,
      clean: !fieldState?.isDirty
    })}
  />
}

function createNumberTextField (fieldId: string, label: string, field: ControllerRenderProps<FieldValues, string>, fieldState: ControllerFieldState): ReactNode {
  return <TextField
    {...field}
    id={`${fieldId}-text-field`}
    key={`${fieldId}-text-field`}
    InputProps={{
      inputProps: {}
    }}
    label={label}
    type="number"
    size="small"
    sx={{ width: '150px' }}
    value={field.value ?? ''}
    onChange={(event) => {
      field.onChange(Number.parseFloat(event.target.value))
    }}
    InputLabelProps={{
      shrink: true
    }}
    className={clsx('fieldState', {
      dirty: fieldState?.isDirty,
      clean: !fieldState?.isDirty
    })}
  />
}

function createSwitch (fieldId: string, label: string, field: ControllerRenderProps<FieldValues, string>, fieldState: ControllerFieldState): ReactNode {
  return <FormGroup
    sx={{ width: '150px', margin: 0 }}
  >
    <FormControlLabel
      label={ <Typography fontSize={11} sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{label}</Typography> }
      disableTypography={true}
      control={
        <Switch
          {...field}
          id={`${fieldId}-switch`}
          key={`${fieldId}-switch`}
          checked={field.value ?? false}
          onChange={(event) => {
            field.onChange(event.target.checked)
          }}
          className={clsx('fieldState', {
            dirty: fieldState?.isDirty,
            clean: !fieldState?.isDirty
          })}
        />
      }
    />
  </FormGroup>
}

const StrategyComponentParameterCard: React.FC<StrategyComponentParameterCardProps> = (props: StrategyComponentParameterCardProps & { children?: ReactNode }) => {
  const theme = useTheme()
  const { strategy, parameter, value } = props
  let parameterDisplayName: string = parameter
  if (parameter.includes('.')) {
    const splits = parameter.split('.')
    parameterDisplayName = splits[splits.length - 1]
  }
  const fieldId = `${strategy?.name ?? ''}-${parameter}`

  const { field, fieldState } = useController(props)

  const node = useMemo(() => {
    let labelledValueNode = null
    if (typeof value === 'string') {
      labelledValueNode = createStringTextField(fieldId, parameterDisplayName, field, fieldState)
    } else if (typeof value === 'boolean') {
      labelledValueNode = createSwitch(fieldId, parameterDisplayName, field, fieldState)
    } else if (typeof value === 'number') {
      labelledValueNode = createNumberTextField(fieldId, parameterDisplayName, field, fieldState)
    }

    return labelledValueNode
  }, [field.value, fieldState.isDirty])

  return (
    <Tooltip title={parameter} placement="top" arrow enterDelay={300} enterNextDelay={300}>
      <Box
        sx={{
          '& .fieldState.dirty': {
            backgroundColor: theme.palette.error.dark
          },
          '& .fieldState.clean': {
            backgroundColor: 'transparent'
          }
        }}
      >
        {node}
      </Box>
    </Tooltip>
  )
}

export default StrategyComponentParameterCard
